import React, { useMemo } from 'react';

const WIDTH = 320;
const HEIGHT = 38;
const PAD = 4;

/**
 * Maps values onto the viewBox. A flat series sits on the baseline rather than
 * dividing by a zero range.
 * @param {Array<number>} values - Ordered daily values.
 * @returns {Array<{x: number, y: number}>} Plotted points.
 */
const plot = (values) => {
  const low = Math.min(...values);
  const high = Math.max(...values);
  const range = high - low;
  const step = WIDTH / (values.length - 1);

  return values.map((value, index) => ({
    x: index * step,
    y: range === 0 ? HEIGHT - PAD : PAD + (HEIGHT - 2 * PAD) * (1 - (value - low) / range),
  }));
};

/**
 * Fourteen days of daily consumption, drawn inline on a meter card.
 *
 * Deliberately axis-less and label-less: it answers "is this meter behaving as
 * usual?" at a glance, and the details page carries the readable charts.
 *
 * @param {Array<number>} values - Ordered daily values.
 * @param {string} tone - 'good', 'warn' or 'bad'; picks the stroke colour.
 * @param {string} label - Accessible description of the trend.
 */
const Sparkline = ({ values, tone = 'good', label }) => {
  const geometry = useMemo(() => {
    const points = plot(values);
    const line = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(0)} ${point.y.toFixed(1)}`)
      .join(' ');

    return {
      line,
      area: `${line} L${WIDTH} ${HEIGHT} L0 ${HEIGHT} Z`,
      last: points[points.length - 1],
    };
  }, [values]);

  return (
    <div className={`im-spark im-spark--${tone}`}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
      >
        <path className="im-spark-area" d={geometry.area} />
        <path className="im-spark-line" d={geometry.line} fill="none" />
        <circle
          className="im-spark-dot"
          cx={geometry.last.x.toFixed(0)}
          cy={geometry.last.y.toFixed(1)}
          r="3"
        />
      </svg>
    </div>
  );
};

export default Sparkline;
